'use client';

import { useTypeSequence } from '@/helpers/useTypeSequence';
import { css } from '@onbeam/business-styled-system/css';
import { center, text, vstack } from '@onbeam/business-styled-system/patterns';
import { token } from '@onbeam/business-styled-system/tokens';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const excludedRoutes = ['/contact'];

const baseColors = [
  token('colors.blue.0'),
  token('colors.red.0'),
  token('colors.yellow.0'),
  token('colors.green.0'),
];

const timedLyrics = [
  { time: 0, word: 'no' },
  { time: 250, word: 'merit' },
  { time: 700, word: 'circle' },
  { time: 1200, word: 'beam' },
  { time: 1600, word: 'beam' },
  { time: 2000, word: 'beam' },
  { time: 2500, word: 'beam' },
  { time: 2900, word: 'beam' },
  { time: 3300, word: 'beam' },
  { time: 3700, word: 'beam' },
  { time: 4200, word: 'beam' },
  { time: 4600, word: 'beam' },
  { time: 5100, word: null },
];

export const NoMeritCircle = () => {
  const pathname = usePathname();
  const audioRef = useRef<HTMLAudioElement>(null);
  const timers = useRef<NodeJS.Timeout[]>([]);
  const [currentWord, setCurrentWord] = useState<string | null>(null);
  const [colorCycle, setColorCycle] = useState<string[]>(baseColors);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    audioRef.current = new Audio('/audio/beam.mp3');
    return () => {
      timers.current.forEach(clearTimeout);
    };
  }, []);

  useTypeSequence('merit', () => {
    if (excludedRoutes.includes(pathname) || isPlaying) return;

    audioRef.current?.play().then(() => {
      let lastWord: string | null = null;

      timers.current = timedLyrics.flatMap(({ time, word }, index) => {
        const operations: NodeJS.Timeout[] = [];

        if (word === lastWord && word !== null) {
          operations.push(setTimeout(() => setCurrentWord(null), time - 50));
        }

        operations.push(
          setTimeout(() => {
            if (index === 0) setIsPlaying(true);
            setCurrentWord(word);

            if (word === 'beam') {
              setColorCycle((prev) => {
                const [first, ...rest] = prev;
                return [...rest, first];
              });
            }

            if (index === timedLyrics.length - 1) {
              setIsPlaying(false);
              setColorCycle(baseColors);
            }
          }, time),
        );

        lastWord = word;
        return operations;
      });
    });
  });

  if (!isPlaying) return null;

  return (
    <div
      className={center({
        position: 'fixed',
        inset: '0',
        zIndex: 999,
        overflow: 'hidden',
        transition: 'colors',
        bg: 'mono.primaryFg/60',
        backdropFilter: '[blur(10px)]',
      })}
    >
      {(currentWord === 'beam' || currentWord === null) && (
        <div
          className={vstack({
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            pointerEvents: 'none',
            userSelect: 'none',
            opacity: 0.9,
          })}
        >
          {colorCycle.map((color) => (
            <div
              aria-hidden
              key={color}
              className={css({
                flex: '1',
              })}
              style={{
                backgroundColor: color,
              }}
            />
          ))}
        </div>
      )}
      {currentWord && (
        <span
          key={currentWord + Math.random()}
          className={text({
            style: 'headline-xxl',
            fontSize: '20vw',
            textTransform: 'uppercase',
            position: 'relative',
            zIndex: 10,
          })}
        >
          {currentWord}
        </span>
      )}
    </div>
  );
};
